import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Grid, Image, Segment, Header, Table, Button, Icon,
  Label, Message, Tab, Dropdown, Popup
} from 'semantic-ui-react';
import {
  fetchActorApplications,
  selectActorApplications,
  selectApplicationsStatus,
  selectApplicationsError
} from '../../slices/applicationSlice';
import {
  fetchUserConversations,
  setActiveConversation,
  selectConversations,
  selectCurrentConversation,
  selectMessagesLoading,
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  selectNotifications,
  selectNotificationUnreadCount,
  selectNotificationsLoading
} from '../../slices/messageSlice';
import ConversationList from '../../components/messaging/MessageCenter/ConversationList';
import ConversationView from '../../components/messaging/Conversation/ConversationView';
import NotificationList from '../../components/messaging/MessageCenter/NotificationList';
import hourGlass from '../../assets/hourGlassLine.svg';
import { AuthStdFooter } from "../../utils/auth-std-footer";
import { format } from 'date-fns';

// ============================================================================
// STATUS HELPERS
// ============================================================================

const STATUS_COLORS = {
  'Pending': 'yellow',
  'Accepted': 'green',
  'Rejected': 'red',
  'Withdrawn': 'grey'
};

const getStatusColor = (status) => STATUS_COLORS[status] || 'grey';

const STATUS_OPTIONS = [
  { key: 'all', value: 'all', text: 'All Statuses' },
  { key: 'Pending', value: 'Pending', text: 'Pending' },
  { key: 'Accepted', value: 'Accepted', text: 'Accepted' },
  { key: 'Rejected', value: 'Rejected', text: 'Rejected' }
];

// ============================================================================
// SORTABLE TABLE HEADER
// ============================================================================

const SortableHeader = ({ label, field, sortField, sortDirection, onSort }) => {
  const isActive = sortField === field;
  return (
    <Table.HeaderCell
      onClick={() => onSort(field)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      {label}
      {isActive && (
        <Icon name={sortDirection === 'asc' ? 'sort up' : 'sort down'} style={{ marginLeft: 4 }} />
      )}
      {!isActive && <Icon name="sort" style={{ marginLeft: 4, opacity: 0.3 }} />}
    </Table.HeaderCell>
  );
};

// ============================================================================
// DATE FORMATTER
// ============================================================================

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
};

// ============================================================================
// APPLICATION TABLE COMPONENT
// ============================================================================

const ApplicationTable = ({ applications, navigate, emptyMessage, grouped = false }) => {
  const [sortField, setSortField] = useState(grouped ? 'litGroup' : 'submissionDate');
  const [sortDirection, setSortDirection] = useState(grouped ? 'asc' : 'desc');
  const [statusFilter, setStatusFilter] = useState('all');

  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  // Accessor helpers to pull nested data safely
  const getFieldValue = useCallback((app, field) => {
    switch (field) {
      case 'litGroup': return app.litGroup?.name || '';
      case 'book': return app.book?.name || app.book?.title || '';
      case 'character': return app.character?.name || '';
      case 'opportunity': return app.opportunity?.title || '';
      case 'status': return app.status || '';
      case 'submissionDate': return app.submissionDate || app.createdAt || '';
      default: return '';
    }
  }, []);

  // When grouped, always sort by LitGroup → Book → Character for proper rowSpan
  const filteredAndSorted = useMemo(() => {
    let result = [...applications];

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter(app => app.status === statusFilter);
    }

    if (grouped) {
      // Hierarchical sort: LitGroup → Book → Character
      result.sort((a, b) => {
        const aLg = (a.litGroup?.name || '').toLowerCase();
        const bLg = (b.litGroup?.name || '').toLowerCase();
        if (aLg !== bLg) return aLg < bLg ? -1 : 1;

        const aBook = (a.book?.name || a.book?.title || '').toLowerCase();
        const bBook = (b.book?.name || b.book?.title || '').toLowerCase();
        if (aBook !== bBook) return aBook < bBook ? -1 : 1;

        const aChar = (a.character?.name || '').toLowerCase();
        const bChar = (b.character?.name || '').toLowerCase();
        return aChar < bChar ? -1 : aChar > bChar ? 1 : 0;
      });
    } else {
      // Normal sort by selected column
      result.sort((a, b) => {
        const aVal = getFieldValue(a, sortField);
        const bVal = getFieldValue(b, sortField);

        if (sortField === 'submissionDate') {
          const aDate = new Date(aVal || 0);
          const bDate = new Date(bVal || 0);
          return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
        }

        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [applications, statusFilter, sortField, sortDirection, getFieldValue, grouped]);

  // Compute rowSpan maps for grouped mode
  const { litGroupSpans, bookSpans } = useMemo(() => {
    if (!grouped) return { litGroupSpans: {}, bookSpans: {} };

    const lgSpans = {}; // index → rowSpan for LitGroup cell
    const bkSpans = {}; // index → rowSpan for Book cell

    let i = 0;
    while (i < filteredAndSorted.length) {
      const lgName = filteredAndSorted[i].litGroup?.name || '';

      // Find how many consecutive rows share this LitGroup
      let lgEnd = i + 1;
      while (lgEnd < filteredAndSorted.length &&
        (filteredAndSorted[lgEnd].litGroup?.name || '') === lgName) {
        lgEnd++;
      }
      lgSpans[i] = lgEnd - i;

      // Within this LitGroup span, find Book sub-groups
      let j = i;
      while (j < lgEnd) {
        const bookName = filteredAndSorted[j].book?.name || filteredAndSorted[j].book?.title || '';
        let bkEnd = j + 1;
        while (bkEnd < lgEnd &&
          (filteredAndSorted[bkEnd].book?.name || filteredAndSorted[bkEnd].book?.title || '') === bookName) {
          bkEnd++;
        }
        bkSpans[j] = bkEnd - j;
        j = bkEnd;
      }

      i = lgEnd;
    }

    return { litGroupSpans: lgSpans, bookSpans: bkSpans };
  }, [filteredAndSorted, grouped]);

  if (applications.length === 0) {
    return (
      <Message info>
        <Message.Header>No Applications</Message.Header>
        <p>{emptyMessage}</p>
      </Message>
    );
  }

  // Shared row renderer (Character, Status, Submitted, Actions)
  const renderRowCells = (app) => {
    const progress = app.recordingProgress || { total: 0, recorded: 0, waiting: 0 };
    return (
      <>
        <Table.Cell>
          {app.character?.name || '—'}
        </Table.Cell>
        <Table.Cell>
          <div>
            <Label color={getStatusColor(app.status)} size="small">
              {app.status || 'Unknown'}
            </Label>
          </div>
          {app.status === 'Accepted' && progress.total > 0 && (
            <div style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <Label size="mini" color="yellow" basic>
                <Icon name="clock" /> Waiting: {progress.waiting}
              </Label>
              <Label size="mini" color="green" basic>
                <Icon name="check" /> Recorded: {progress.recorded}
              </Label>
            </div>
          )}
        </Table.Cell>
        <Table.Cell>
          {formatDate(app.submissionDate)}
        </Table.Cell>
        <Table.Cell>
          <Button.Group size="small">
            <Popup
              content="View application details"
              trigger={
                <Button icon="eye" onClick={() => navigate(`/applications/${app.uuid}`)} />
              }
            />
            <Popup
              content="View opportunity"
              trigger={
                <Button
                  icon="bullhorn"
                  onClick={() => navigate(`/opportunities/${app.opportunity?.uuid}`, {
                    state: { from: '/dashboard', backButtonText: 'Back to My Applications' }
                  })}
                  disabled={!app.opportunity?.uuid}
                />
              }
            />
            {app.status === 'Accepted' && (
              <Popup
                content="Go to Recording Studio"
                trigger={
                  <Button icon="microphone" color="green" onClick={() => navigate(`/recording-studio/${app.uuid}`)} />
                }
              />
            )}
          </Button.Group>
        </Table.Cell>
      </>
    );
  };

  return (
    <>
      {/* Status filter — only for non-grouped (Applications tab) */}
      {!grouped && (
        <div style={{ marginBottom: '1em', display: 'flex', alignItems: 'center', gap: '1em' }}>
          <span style={{ fontWeight: 500 }}>Filter by status:</span>
          <Dropdown
            selection
            compact
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(e, { value }) => setStatusFilter(value)}
          />
          <span style={{ color: '#888', fontSize: '0.9em' }}>
            Showing {filteredAndSorted.length} of {applications.length} applications
          </span>
        </div>
      )}

      <Table celled structured={grouped} sortable={!grouped} selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>LitGroup</Table.HeaderCell>
            <Table.HeaderCell>Book</Table.HeaderCell>
            {!grouped ? (
              <>
                <SortableHeader label="Character" field="character" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Status" field="status" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Submitted" field="submissionDate" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
              </>
            ) : (
              <>
                <Table.HeaderCell>Character</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Submitted</Table.HeaderCell>
              </>
            )}
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {filteredAndSorted.map((app, idx) => {
            const showLitGroup = grouped ? litGroupSpans[idx] !== undefined : true;
            const showBook = grouped ? bookSpans[idx] !== undefined : true;

            return (
              <Table.Row key={app.uuid}>
                {/* LitGroup cell — with rowSpan in grouped mode */}
                {showLitGroup && (
                  <Table.Cell
                    rowSpan={grouped ? litGroupSpans[idx] : undefined}
                    style={grouped && litGroupSpans[idx] > 1 ? {
                      verticalAlign: 'top',
                      fontWeight: 'bold',
                      backgroundColor: '#f9fafb'
                    } : undefined}
                  >
                    {app.litGroup?.name || <span style={{ color: '#999' }}>—</span>}
                  </Table.Cell>
                )}
                {/* Book cell — with rowSpan in grouped mode */}
                {showBook && (
                  <Table.Cell
                    rowSpan={grouped ? bookSpans[idx] : undefined}
                    style={grouped && bookSpans[idx] > 1 ? {
                      verticalAlign: 'top',
                      backgroundColor: '#fcfcfc'
                    } : undefined}
                  >
                    <strong>{app.book?.name || app.book?.title || '—'}</strong>
                  </Table.Cell>
                )}
                {/* Remaining cells */}
                {renderRowCells(app)}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const MyAssignments = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get the current user and actor ID (same pattern as OpportunityDetails.jsx)
  const currentUser = useSelector(state => state.user.profile?.data);
  const actorId = currentUser?.userId;

  // Get applications from Redux
  const applications = useSelector(selectActorApplications(actorId || ''));
  const appStatus = useSelector(selectApplicationsStatus);
  const appError = useSelector(selectApplicationsError);

  // Messaging state
  const conversations = useSelector(selectConversations);
  const currentConversation = useSelector(selectCurrentConversation);
  const messagesLoading = useSelector(selectMessagesLoading);
  const notifications = useSelector(selectNotifications);
  const notificationUnreadCount = useSelector(selectNotificationUnreadCount);
  const notificationsLoading = useSelector(selectNotificationsLoading);

  // Messaging handlers
  const handleConversationSelect = useCallback((conversation, threadId = null) => {
    dispatch(setActiveConversation({ ...conversation, activeThreadId: threadId }));
  }, [dispatch]);

  const handleBackToList = useCallback(() => {
    dispatch(setActiveConversation(null));
  }, [dispatch]);

  const handleMarkNotificationRead = useCallback((notificationId) => {
    dispatch(markNotificationRead(notificationId));
  }, [dispatch]);

  const handleMarkAllNotificationsRead = useCallback(() => {
    if (actorId) dispatch(markAllNotificationsRead(actorId));
  }, [dispatch, actorId]);

  // Fetch applications and messaging data on mount
  useEffect(() => {
    if (actorId) {
      dispatch(fetchActorApplications(actorId));
      dispatch(fetchUserConversations(actorId));
      dispatch(fetchNotifications(actorId));
    }
  }, [dispatch, actorId]);

  // Separate accepted (assignments) from pending/rejected applications
  const acceptedApplications = useMemo(
    () => applications.filter(app => app.status === 'Accepted'),
    [applications]
  );
  const pendingApplications = useMemo(
    () => applications.filter(app => app.status !== 'Accepted'),
    [applications]
  );

  // Tab panes
  const panes = [
    {
      menuItem: {
        key: 'applications',
        icon: 'file alternate',
        content: `My Applications (${pendingApplications.length})`
      },
      render: () => (
        <Tab.Pane>
          <ApplicationTable
            applications={pendingApplications}
            navigate={navigate}
            emptyMessage="You don't have any pending applications. Browse the Opportunity Marketplace to find voice acting roles, or check the Assignments tab for accepted applications."
          />
          {pendingApplications.length === 0 && (
            <div style={{ textAlign: 'center', marginTop: '1em' }}>
              <Button
                primary
                icon="shopping bag"
                content="Browse Opportunities"
                onClick={() => navigate('/opportunities')}
              />
            </div>
          )}
        </Tab.Pane>
      )
    },
    {
      menuItem: {
        key: 'assignments',
        icon: 'tasks',
        content: `My Assignments (${acceptedApplications.length})`
      },
      render: () => (
        <Tab.Pane>
          <ApplicationTable
            applications={acceptedApplications}
            navigate={navigate}
            emptyMessage="You don't have any accepted assignments yet. Your accepted applications will appear here."
            grouped
          />
        </Tab.Pane>
      )
    },
    {
      menuItem: {
        key: 'messages',
        icon: 'comments',
        content: `Messages (${conversations.length})`
      },
      render: () => (
        <Tab.Pane style={{ minHeight: '400px' }}>
          <Grid>
            <Grid.Column width={5} style={{ borderRight: '1px solid #ddd', paddingRight: 0 }}>
              <ConversationList
                conversations={conversations}
                onConversationSelect={handleConversationSelect}
                selectedConversationId={currentConversation?.conversationId}
                loading={messagesLoading?.conversations}
              />
            </Grid.Column>
            <Grid.Column width={11}>
              {currentConversation ? (
                <ConversationView
                  conversation={currentConversation}
                  onBack={handleBackToList}
                  showBackButton={true}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '3em 1em', color: '#999' }}>
                  <Icon name="comments outline" size="huge" />
                  <Header as="h3" style={{ color: '#999' }}>Select a conversation</Header>
                  <p>Choose a conversation from the list to view messages.</p>
                </div>
              )}
            </Grid.Column>
          </Grid>
        </Tab.Pane>
      )
    },
    {
      menuItem: {
        key: 'notifications',
        icon: 'bell',
        content: (
          <span>
            Notifications
            {notificationUnreadCount > 0 && (
              <Label circular color="red" size="mini" style={{ marginLeft: 6 }}>
                {notificationUnreadCount}
              </Label>
            )}
          </span>
        )
      },
      render: () => (
        <Tab.Pane style={{ minHeight: '400px' }}>
          <NotificationList
            notifications={notifications}
            loading={notificationsLoading}
            onMarkRead={handleMarkNotificationRead}
            onMarkAllRead={handleMarkAllNotificationsRead}
          />
        </Tab.Pane>
      )
    }
  ];

  return (
    <div className="listContainer">
      <div style={{ marginTop: "30px", marginBottom: "20px" }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={3}>
              <Image src={hourGlass} size={"small"} style={{ height: "120px" }} />
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">My Activity History</span>
              <span className="blgListSubTitle">Track your voice acting applications, assignments, messages and notifications</span>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>

      {/* Loading state */}
      {appStatus === 'loading' && (
        <Message icon>
          <Icon name="circle notched" loading />
          <Message.Content>
            <Message.Header>Loading</Message.Header>
            Fetching your applications...
          </Message.Content>
        </Message>
      )}

      {/* Error state */}
      {appStatus === 'failed' && (
        <Message negative>
          <Message.Header>Error Loading Applications</Message.Header>
          <p>{typeof appError === 'string' ? appError : 'An error occurred while fetching your applications. Please try again.'}</p>
          <Button
            icon="refresh"
            content="Retry"
            onClick={() => actorId && dispatch(fetchActorApplications(actorId))}
          />
        </Message>
      )}

      {/* Not logged in state */}
      {!actorId && appStatus !== 'loading' && (
        <Message warning>
          <Message.Header>Not Logged In</Message.Header>
          <p>Please log in to view your applications and assignments.</p>
        </Message>
      )}

      {/* Main content — tabs */}
      {actorId && appStatus !== 'loading' && (
        <Segment style={{ marginBottom: "70px" }}>
          <Tab panes={panes} />
        </Segment>
      )}

      {AuthStdFooter()}
    </div>
  );
};

export default MyAssignments;
